import { userApi } from "../../../redux/api/axiosInstance";

interface userPayload {
  Name: string;
  Email: string;
  NewPassword: string;
  ContactNumber: string;
  ProfileImage: File | null;
  RoleId: number;
  user_type?: string;
}

export interface CandidateSignupPayload {
  Name: string;
  Email: string;
  NewPassword: string;
  ContactNumber: string;
  ProfileImage?: File | null;
}

/**
 * Server-side list controls for GET /Users (UM, 2026-09-23). All optional and additive.
 * `sortBy` is a UM `UserListSortField` name: Id | Name | Email | Status.
 */
export interface UserListControls {
  roleId?: number;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const getAllUsers = async (page = 1, pageSize = 10, search?: string, controls: UserListControls = {}) => {
  try {
    let url = `/Users?pageNo=${page}&pageSize=${pageSize}`;
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    if (controls.roleId) url += `&roleId=${controls.roleId}`;
    if (controls.isActive !== undefined) url += `&isActive=${controls.isActive}`;
    if (controls.sortBy) {
      url += `&sortBy=${encodeURIComponent(controls.sortBy)}&sortDir=${controls.sortDir ?? 'asc'}`;
    }
    const response = await userApi.get(url);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching all employees:', error);
    throw error;
  }
};

export const addUser = async (payload: userPayload) => {
  try {
    const formData = new FormData();
    formData.append('Name', payload.Name);
    formData.append('Email', payload.Email);
    formData.append('NewPassword', payload.NewPassword);
    formData.append('ContactNumber', payload.ContactNumber || '');
    formData.append('RoleId', payload.RoleId.toString());
    if (payload.user_type) {
      formData.append('user_type', payload.user_type);
    }
    if (payload.ProfileImage && payload.ProfileImage instanceof File) {
      formData.append('ProfileImage', payload.ProfileImage, payload.ProfileImage.name);
    }

    const response = await userApi.post(
      'Users',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error Add Users', error);
    throw error;
  }
};

export interface UserWithEmployeePayload extends userPayload {
  IsEmployee: boolean;
  // Employee detail fields (only sent when IsEmployee is true)
  FirstName?: string;
  LastName?: string;
  Gender?: string;
  DateOfBirth?: string;
  HireDate?: string;
  JobTitle?: string;
  DepartmentId?: number | null;
  DesignationId?: number | null;
  ManagerId?: number | null;
  Status?: string;
  EmploymentType?: string;
  WorkLocation?: string;
  Salary?: number | null;
  EmergencyContact?: string;
  EmergencyContactRelation?: string;
  Nationality?: string;
  MaritalStatus?: string;
  BloodGroup?: string;
  DisabilityStatus?: string;
  MinorityGroup?: string;
  Address?: string;
  City?: string;
  State?: string;
  Country?: string;
  EmployeeNumber?: string;
  IdNumb?: string;
  IsTeamLead?: boolean;
}

export interface AddUserWithEmployeeResponse {
  success: boolean;
  message: string;
  data: {
    userId: number;
    name: string;
    email: string;
    roleId: number;
    isEmployee: boolean;
    /** Present only when IsEmployee was true. */
    employeeId?: number | null;
  };
}

// Combined "create user (+ optional employee)" flow. When IsEmployee is true the
// backend also creates a linked Employees row (by email) and assigns the Employee
// role — both inside ONE database transaction, so this can never leave a user
// without their employee record (or the reverse). This is the single write path
// for "a new person who needs a login", used by both /users and /employees.
export const addUserWithEmployee = async (payload: UserWithEmployeePayload) => {
  try {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'ProfileImage') return;
      if (value === undefined || value === null) return;
      formData.append(key, String(value));
    });
    if (payload.ProfileImage && payload.ProfileImage instanceof File) {
      formData.append('ProfileImage', payload.ProfileImage, payload.ProfileImage.name);
    }

    const response = await userApi.post('Users/AddUserWithEmployee', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as AddUserWithEmployeeResponse;
  } catch (error: any) {
    console.error('Error adding user with employee', error);
    throw error;
  }
};

export const getAllRoles = async () => {
  try {
    const response = await userApi.get('Users/roles');
    return response;
  } catch (error: any) {
    console.error('Error fetching roles:', error);
    throw error;
  }
}

/** Active accounts per role id ({ "12": 4 }); a role with no holder is absent. */
export const getRoleHolderCounts = async (): Promise<Record<string, number>> => {
  const response = await userApi.get('Roles/holder-counts');
  return response.data ?? {};
};

export const addRole = async (roleName: string) => {
  try {
    const response = await userApi.post('Users/AddRole', JSON.stringify(roleName), {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error adding role:', error);
    throw error;
  }
};

export const deleteRole = async (roleId: number) => {
  try {
    const response = await userApi.delete(`Users/Deleterole/${roleId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting role:', error);
    throw error;
  }
};

export const activateUser = async (userId: number, isActive: boolean) => {
  try {
    const response = await userApi.post('Users/activate-user', { userId, isActive }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error activating/deactivating user:', error);
    throw error;
  }
};

export const assignUserRole = async (userId: number, roleId: number) => {
  try {
    const response = await userApi.post('Users/AssignUserRole', { userId, roleId }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error assigning role to user:', error);
    throw error;
  }
};

export const addCandidateSignup = async (payload: CandidateSignupPayload) => {
  try {
    const formData = new FormData();
    formData.append('Name', payload.Name);
    formData.append('Email', payload.Email);
    formData.append('NewPassword', payload.NewPassword);
    formData.append('ContactNumber', payload.ContactNumber || '');
    if (payload.ProfileImage) {
      formData.append('ProfileImage', payload.ProfileImage);
    }

    const response = await userApi.post(
      'Users/SignUpforCandidate',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data;
  } catch (error: any) {
    console.error('Error signing up candidate:', error);
    throw error;
  }
};
export const unAssignUserRole = async (userId: number, roleId: number) => {
  try {
    const response = await userApi.post('Users/UnAssignUserRole', { userId, roleId }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error assigning role to user:', error);
    throw error;
  }
};

export const getUserProfile = async () => {
  try {
    const response = await userApi.get('Users/Profile');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const getUserById = async (id: number) => {
  try {
    const response = await userApi.get(`Users/GetUserbyId/${id}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching user by ID:', error);
    throw error;
  }
};

export const updateProfile = async (payload: {
  id: number;
  name?: string;
  contactNumber?: string;
  profileImage?: File | null;
}) => {
  try {
    const formData = new FormData();
    // Id is required by the backend - without it the server returns 401
    formData.append('Id', String(payload.id));
    // Backend field is 'FullName', not 'Name'
    if (payload.name) formData.append('FullName', payload.name);
    if (payload.contactNumber) formData.append('ContactNumber', payload.contactNumber);
    if (payload.profileImage) formData.append('ProfileImage', payload.profileImage);

    const response = await userApi.put('Users/update-profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

export const updatePassword = async (payload: {
  userId: number;
  newPassword: string;
  confirmPassword: string;
}) => {
  try {
    const response = await userApi.put('Users/update-password', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error updating password:', error);
    throw error;
  }
};

export const selfUpdatePassword = async (payload: {
  userId: number;
  newPassword: string;
  currentPassword: string;
}) => {
  try {
    const response = await userApi.put('Users/self-update-password', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error self updating password:', error);
    throw error;
  }
};

export const deleteUser = async (id: number) => {
  try {
    const response = await userApi.delete(`Users/${id}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const verifyEmail = async (payload: { email: string; otp: string; tenant?: string }) => {
  try {
    const response = await userApi.post('Users/verify-email', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error verifying email:', error);
    throw error;
  }
};

export const sendResetLink = async (payload: { email: string; tenant?: string }) => {
  try {
    const response = await userApi.post('Users/send-reset-link', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error sending reset link:', error);
    throw error;
  }
};

export const resetPassword = async (payload: { email: string; token: string; newPassword: string; tenant?: string }) => {
  try {
    const response = await userApi.post('Users/reset-password', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

export const bulkUploadUsers = async (file: File) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await userApi.post('Users/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error bulk uploading users:', error);
    throw error;
  }
};

export const downloadUserCsvTemplate = async (): Promise<Blob> => {
  try {
    const response = await userApi.get('Users/download-user-csv-template', {
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    console.error('Error downloading user CSV template:', error);
    throw error;
  }
};
/** docs/85 6.5: always answers 202 with one body, whether or not the email has a workspace. */
export const findWorkspace = async (email: string) => {
  const response = await userApi.post('tenants/find-workspace', { email });
  return response.data;
};
